import { AuthiculateClient } from '@articulate/authiculate-sdk'
import createDebug from 'debug'
import log from 'loglevel'

import { buildAuthState } from './util'

type AuthiculateTokensResponse = ReturnType<AuthiculateClient['fetchTokensWithAuthorizationCode']>

export const CALLBACK_PATH = '/auth-callback'

const redirectUri = `${window.location.protocol}//${window.location.host}${CALLBACK_PATH}`
const scopes = ['openid', 'profile_name', 'training_admin', 'email', 'user_subscription']

const debug = createDebug('360:auth:authiculate')
const authiculateClientLogger = log.getLogger('authiculate-sdk')
authiculateClientLogger.setLevel(log.levels.SILENT) // Make the authiculate-sdk logger silent because it logs errors that are expected and already handled here

const authiculateClient = new AuthiculateClient({
  clientId: process.env.OKTA_CLIENT_ID,
  issuer: process.env.OIDC_ISSUER_URI,
  redirectUri,
  logger: authiculateClientLogger
})

let redirectingForLogin = false
let signInNoPromptPromise: AuthiculateTokensResponse = null

const signIn = (currentPath: string, stateObject = {}) => {
  debug('signIn', { currentPath, redirectingForLogin, state: stateObject })

  if (redirectingForLogin) {
    return
  }

  redirectingForLogin = true

  const state = buildAuthState(stateObject)

  window.localStorage.setItem(state, currentPath)

  debug('Initiating sign in via getWithRedirect')

  return authiculateClient.getAuthCodeWithRedirect({ scopes, state })
}

const signInNoPrompt = () => {
  debug('signInNoPrompt', { alreadyInFlight: !!signInNoPromptPromise })

  if (!signInNoPromptPromise) {
    debug('Initiating sign in via getWithoutPrompt')

    signInNoPromptPromise =
      authiculateClient
        .getAuthCodeWithIframe({ scopes, prompt: 'none' })
        .then(code => authiculateClient.fetchTokensWithAuthorizationCode(code))
        .finally(() => signInNoPromptPromise = null)
  }

  return signInNoPromptPromise
}

const signOut = () => {
  debug('Closing session')

  return authiculateClient.deleteSession()
}

const clearTokens = () => {
  debug('Clearing tokens from token manager')

  return authiculateClient.getStorage().clearTokens()
}

const getSession = () => authiculateClient.fetchSession()

const refreshSession = () => authiculateClient.refreshSession()

export {
  authiculateClient,
  clearTokens,
  getSession,
  refreshSession,
  scopes,
  signIn,
  signInNoPrompt,
  signOut
}
